import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

const STORAGE_KEYS = {
    state: 'oauth_state',
    codeVerifier: 'oauth_code_verifier',
    scope: 'oauth_scope',
    returnTo: 'oauth_return_to',
};

const DEFAULT_SCOPE = 'timekeeper schedule employee auth mdm_psc custom_reports';

const toBase64Url = (input) =>
    btoa(String.fromCharCode(...new Uint8Array(input)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

const randomString = (bytes = 32) => {
    const array = new Uint8Array(bytes);
    window.crypto.getRandomValues(array);
    return toBase64Url(array);
};

const createCodeChallenge = async (codeVerifier) => {
    const data = new TextEncoder().encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return toBase64Url(digest);
};

const buildAuthorizeUrl = ({ codeChallenge, state, scope, redirectUri, clientId }) => {
    const authorizeEndpoint = `${process.env.REACT_APP_TK_GW_DOMAIN}/v1/auth/v1/auth/authorize`;

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        scope,
        redirect_uri: redirectUri,
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
    });

    return `${authorizeEndpoint}?${params.toString()}`;
};

export const OAuthAuthorize = () => {
    const [error, setError] = useState('');
    const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);

    useEffect(() => {
        let mounted = true;

        const startAuthorization = async () => {
            try {
                if (!window.crypto?.subtle) {
                    throw new Error('Crypto API no disponible en el navegador');
                }

                const clientId = `${process.env.REACT_APP_CLIENT_ID || ''}`.replace(/"/g, '').trim();
                if (!clientId) {
                    throw new Error('Falta REACT_APP_CLIENT_ID');
                }

                const scope = DEFAULT_SCOPE;
                const redirectUri = `${window.location.origin}/callback`;
                const returnTo = queryParams.get('next') || '/';

                const state = randomString(32);
                const codeVerifier = randomString(64);
                const codeChallenge = await createCodeChallenge(codeVerifier);

                sessionStorage.setItem(STORAGE_KEYS.state, state);
                sessionStorage.setItem(STORAGE_KEYS.codeVerifier, codeVerifier);
                sessionStorage.setItem(STORAGE_KEYS.scope, scope);
                sessionStorage.setItem(STORAGE_KEYS.returnTo, returnTo);

                const authorizeUrl = buildAuthorizeUrl({
                    codeChallenge,
                    state,
                    scope,
                    redirectUri,
                    clientId,
                });

                window.location.assign(authorizeUrl);
            } catch (err) {
                if (!mounted) {
                    return;
                }
                setError(err instanceof Error ? err.message : 'No se pudo iniciar el flujo de autorizacion');
            }
        };

        startAuthorization();

        return () => {
            mounted = false;
        };
    }, [queryParams]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
            }}
        >
            <Stack spacing={2} alignItems='center' sx={{ maxWidth: 560, textAlign: 'center' }}>
                {!error && <CircularProgress size={28} />}
                <Typography variant='h6'>Iniciando autorizacion</Typography>
                <Typography variant='body2' color='text.secondary'>
                    {!error
                        ? 'Te estamos redirigiendo al servidor de autenticacion...'
                        : `No se pudo iniciar el flujo OAuth: ${error}`}
                </Typography>
            </Stack>
        </Box>
    );
};
